import { createContext, useContext, type ReactNode } from 'react';

import { isApiError } from '@platform/api-client';

import { apiClient } from './client';
import type { PlatformClient } from './platform';
import { createPlatformApiClient } from './platform.api';
import { createMockPlatformClient } from './platform.mock';

/**
 * Which `PlatformClient` the super admin screens talk to (MASTER_PLAN M12.7).
 *
 * The same three-mode switch as the admin app's scheduling, pricing and integrations
 * seams, decided once at module load:
 *
 * | Build | `VITE_USE_MOCKS` | Client |
 * |---|---|---|
 * | any | `true` | the mock, and only the mock |
 * | `vite dev` | unset | the real client, falling back to the mock the first time a call cannot reach the API |
 * | `vite build` | unset | the real client, and only the real client |
 *
 * The fallback is compiled out of a production build: a bundle that quietly answered with
 * fixtures when the API was unreachable would show an operator a platform that is not the
 * one they are administering, and on this console that is the most expensive possible
 * lie (rules §17.4). It latches on `NETWORK_ERROR` alone - the code the client invents
 * when `fetch` itself rejected. A `401`, a `403`, a `409` or a `503 MAINTENANCE` came
 * *from* the API and belongs on screen.
 *
 * **Nothing is taken from the mock any more.** `addDomain`, `removeDomain` and
 * `commentOnFeedback` used to be, because no platform route existed for them; all three
 * are mounted now (API.md §2.13), so outside `VITE_USE_MOCKS` and the dev fallback every
 * method on this seam reaches the API.
 */

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';

function defaultClient(): PlatformClient {
  if (useMocks) return createMockPlatformClient();

  const real = createPlatformApiClient(apiClient);
  if (!import.meta.env.DEV) return real;
  return withMockFallback(real, createMockPlatformClient());
}

const PlatformClientContext = createContext<PlatformClient | null>(null);

export function PlatformClientProvider({
  client,
  children,
}: {
  client: PlatformClient;
  children: ReactNode;
}) {
  return <PlatformClientContext.Provider value={client}>{children}</PlatformClientContext.Provider>;
}

/** The app's client, created once. Tests and the provider override it. */
export const platformClient: PlatformClient = defaultClient();

export function usePlatformClient(): PlatformClient {
  return useContext(PlatformClientContext) ?? platformClient;
}

/**
 * One latch, not one per method: a console whose tenant list came from the API and whose
 * plans came from fixtures would be incoherent, and "why does this clinic have no plan"
 * is exactly the question a support engineer must not be made to ask.
 */
function withMockFallback(real: PlatformClient, mock: PlatformClient): PlatformClient {
  type AnyMethod = (...args: never[]) => Promise<unknown>;
  let active: PlatformClient = real;

  const proxy = <Key extends keyof PlatformClient>(method: Key): PlatformClient[Key] =>
    (async (...args: never[]): Promise<unknown> => {
      if (active !== real) return (mock[method] as AnyMethod)(...args);
      try {
        return await (real[method] as AnyMethod)(...args);
      } catch (error) {
        if (!isApiError(error) || error.code !== 'NETWORK_ERROR') throw error;
        console.warn(
          `The API did not answer ${String(method)}; the console is using fixtures for this session.`,
        );
        active = mock;
        return (mock[method] as AnyMethod)(...args);
      }
    }) as PlatformClient[Key];

  const wrapped = {} as Record<string, unknown>;
  for (const method of Object.keys(real) as (keyof PlatformClient)[]) {
    wrapped[method] = proxy(method);
  }
  return wrapped as unknown as PlatformClient;
}
